import React, { useRef, useState } from 'react';

import { type IMergedSlot } from '../../../interface/pu-events';
import { CameraSVG } from '../../utils/svgs';

interface IPhotoAttachProps {
	slot: IMergedSlot;
	onAttach: (slot: IMergedSlot, file: File, onProgress?: (percent: number) => void) => Promise<boolean>;
}

/**
 * Optional post-capture photo evidence: one tap opens the rear camera (or
 * picker), uploads through the media-manager and idempotently re-sends the
 * event with the new mediaUrl. Never blocks or gates the capture itself.
 */
const PhotoAttach = ({ slot, onAttach }: IPhotoAttachProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState(false);

	// Photos attach only to server-confirmed captures (the replay needs the
	// stored referenceId on the backend to merge into).
	if (!slot.confirmed || !slot.referenceId) return null;

	const handleFile = async (file: File | undefined) => {
		if (!file || busy) return;
		setBusy(true);
		setError(false);
		setProgress(0);
		const ok = await onAttach(slot, file, setProgress);
		setBusy(false);
		setError(!ok);
		if (inputRef.current) inputRef.current.value = '';
	};

	return (
		<div className='pue-photo'>
			<input
				ref={inputRef}
				type='file'
				accept='image/*'
				capture='environment'
				className='pue-photo__input'
				onChange={(e) => void handleFile(e.target.files?.[0])}
			/>
			{busy ? (
				<span className='pue-photo__progress'>Uploading photo… {progress}%</span>
			) : (
				<button
					type='button'
					className='pue-photo__btn'
					onClick={() => inputRef.current?.click()}
				>
					<CameraSVG
						width='14'
						height='14'
						color='currentColor'
					/>
					{slot.mediaUrls.length > 0 ? 'Add another photo' : 'Attach photo evidence'}
				</button>
			)}
			{error ? <span className='pue-photo__error'>Upload failed — tap to try again</span> : null}
		</div>
	);
};

export default PhotoAttach;
